import { useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, LayoutGrid } from "lucide-react";
import {
  dayByNumber,
  weekOfDay,
  days as allDays,
} from "@/data/programData";
import AnchorMark from "@/components/AnchorMark";
import ProgramBlocks from "@/components/ProgramBlocks";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";

export default function ProgramDayPage() {
  const { n } = useParams();
  const navigate = useNavigate();
  const num = Number(n);

  const day = useMemo(() => dayByNumber(num), [num]);
  const week = useMemo(() => weekOfDay(num), [num]);

  // Scroll to top whenever day changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [num]);

  if (!day) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F7F5F0" }}>
        <div className="text-center">
          <p
            className="serif text-3xl text-[#15243D] mb-6"
            style={{ fontWeight: 300 }}
          >
            That day doesn’t exist.
          </p>
          <Link to="/program" className="btn-primary">
            Back to overview
          </Link>
        </div>
      </div>
    );
  }

  const prev = num > 1 ? num - 1 : null;
  const next = num < allDays.length ? num + 1 : null;

  return (
    <div
      data-testid="program-day-page"
      className="min-h-screen"
      style={{ backgroundColor: "#F7F5F0" }}
    >
      <Navbar />

      <main className="pt-28 md:pt-36 pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Top nav row */}
          <div className="flex items-center justify-between gap-4 mb-16">
            <Link
              to="/program"
              data-testid="day-overview-link"
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5A5D63] hover:text-[#C07865] transition-colors"
            >
              <LayoutGrid size={14} strokeWidth={1.5} />
              Overview
            </Link>
            <p className="text-xs tracking-[0.2em] uppercase text-[#8A8D93]">
              Week {week?.n} · {week?.name}
            </p>
          </div>

          {/* Heading */}
          <header className="mb-16">
            <div className="flex items-baseline justify-between mb-8">
              <p className="overline" data-testid="day-number">
                Day {String(num).padStart(2, "0")}
              </p>
              <span
                className="serif text-sm tracking-[0.2em] text-[#8A8D93]"
                style={{ fontWeight: 300 }}
              >
                {String(num).padStart(2, "0")} / {String(allDays.length).padStart(2, "0")}
              </span>
            </div>
            <h1
              data-testid="day-title"
              className="serif text-5xl md:text-6xl text-[#15243D] leading-[1.05] tracking-tight"
              style={{ fontWeight: 300 }}
            >
              {day.title}
            </h1>
          </header>

          {/* Body */}
          <article data-testid="day-body" className="mb-20">
            <ProgramBlocks blocks={day.blocks} />
          </article>

          {/* Practice */}
          <section
            data-testid="day-practice"
            className="my-20 pt-12 border-t border-[#15243D]/15"
          >
            <p className="overline mb-6">Day {num} Practice</p>
            {day.practice.intro && (
              <p className="text-lg font-light text-[#15243D] leading-relaxed mb-6">
                {day.practice.intro}
              </p>
            )}
            {day.practice.items?.length > 0 && (
              <ul className="space-y-3 mb-6">
                {day.practice.items.map((it, i) => (
                  <li
                    key={i}
                    className="flex items-baseline gap-3 text-base md:text-lg font-light text-[#15243D] leading-relaxed"
                  >
                    <span className="text-[#C07865] text-xs">—</span>
                    {it}
                  </li>
                ))}
              </ul>
            )}
            {day.practice.closing && (
              <p className="text-base md:text-lg font-light text-[#5A5D63] leading-relaxed">
                {day.practice.closing}
              </p>
            )}
          </section>

          {/* Anchor line */}
          <section
            data-testid="day-anchor-line"
            className="my-20 flex items-start gap-6"
          >
            <AnchorMark size={42} color="#C07865" className="flex-shrink-0 mt-2" />
            <div>
              <p className="overline mb-3">Anchor Line</p>
              <p
                className="serif italic text-2xl md:text-3xl text-[#15243D] leading-snug"
                style={{ fontWeight: 300 }}
              >
                {day.anchor}
              </p>
            </div>
          </section>

          {/* Prev / next nav */}
          <nav
            data-testid="day-pagination"
            className="mt-24 pt-10 border-t border-[#15243D]/15 grid grid-cols-2 gap-4"
          >
            <div>
              {prev !== null ? (
                <Link
                  to={`/program/day/${prev}`}
                  data-testid="day-prev-link"
                  className="group inline-flex flex-col gap-1"
                >
                  <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5A5D63] group-hover:text-[#C07865] transition-colors">
                    <ArrowLeft size={14} strokeWidth={1.5} />
                    Day {prev}
                  </span>
                  <span
                    className="serif text-lg text-[#15243D] group-hover:text-[#C07865] transition-colors"
                    style={{ fontWeight: 300 }}
                  >
                    {dayByNumber(prev)?.title}
                  </span>
                </Link>
              ) : (
                <span />
              )}
            </div>
            <div className="text-right">
              {next !== null ? (
                <Link
                  to={`/program/day/${next}`}
                  data-testid="day-next-link"
                  className="group inline-flex flex-col items-end gap-1"
                >
                  <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5A5D63] group-hover:text-[#C07865] transition-colors">
                    Day {next}
                    <ArrowRight size={14} strokeWidth={1.5} />
                  </span>
                  <span
                    className="serif text-lg text-[#15243D] group-hover:text-[#C07865] transition-colors text-right"
                    style={{ fontWeight: 300 }}
                  >
                    {dayByNumber(next)?.title}
                  </span>
                </Link>
              ) : (
                <Link
                  to="/program"
                  data-testid="day-finished-link"
                  className="inline-flex flex-col items-end gap-1"
                >
                  <span className="text-xs tracking-[0.2em] uppercase text-[#C07865]">
                    Finished
                  </span>
                  <span className="serif text-lg text-[#15243D]" style={{ fontWeight: 300 }}>
                    Return to overview
                  </span>
                </Link>
              )}
            </div>
          </nav>

          {/* Quick jump strip */}
          <div className="mt-16 pt-10 border-t border-[#15243D]/10">
            <p className="overline mb-6">Jump to any day</p>
            <div className="grid grid-cols-7 gap-1.5" data-testid="day-grid">
              {allDays.map((d) => {
                const isCurrent = d.n === num;
                return (
                  <button
                    key={d.n}
                    onClick={() => navigate(`/program/day/${d.n}`)}
                    data-testid={`day-grid-${d.n}`}
                    className={`aspect-square text-xs tracking-[0.1em] transition-colors ${
                      isCurrent
                        ? "bg-[#15243D] text-[#F7F5F0]"
                        : "bg-[#EBE8E0] text-[#15243D] hover:bg-[#C07865] hover:text-white"
                    }`}
                  >
                    {d.n}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
